import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, MenuItem, Paper, Table,
    TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import {
    FeedbackService, FeedbackStatus, FeedbackType, PlatformFeedback,
} from '../../services/feedback.service';
import { InstitutionService } from '../../services/institution.service';
import { IInstitution } from '../../interfaces/institution.interface';
import { useToast } from '../../context/ToastContext';
import { formatDateTime } from '../../utils/dateUtils';
import { SearchableSelect } from '../common/SearchableSelect';
import { FEEDBACK_MAX, FEEDBACK_TYPES } from './FeedbackDialog';
import { FEEDBACK_STATUSES, FeedbackStatusChip } from './FeedbackStatusChip';
import { typeLabel } from './MyFeedbackPage';
import { FeedbackAttachmentChips } from './FeedbackAttachmentChips';

/** Local yyyy-mm-dd → the instant that day starts (plusDays shifts it, so "to" can be the exclusive next day). */
export const dayStartIso = (day: string, plusDays = 0): string | undefined => {
    if (!day) return undefined;
    const d = new Date(`${day}T00:00:00`);
    d.setDate(d.getDate() + plusDays);
    return d.toISOString();
};

/** Kervolt team inbox: every user's feedback across institutions, with reply + status. Platform roles only. */
const PlatformFeedbackPage: React.FC = () => {
    const toast = useToast();
    const [rows, setRows] = useState<PlatformFeedback[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(20);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState<FeedbackStatus | ''>('NEW');
    const [type, setType] = useState<FeedbackType | ''>('');
    const [institutionId, setInstitutionId] = useState('');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [institutions, setInstitutions] = useState<IInstitution[]>([]);
    const [editing, setEditing] = useState<PlatformFeedback | null>(null);

    useEffect(() => {
        InstitutionService.getInstitutions(0, 200)
            .then(r => setInstitutions(r?.content ?? []))
            .catch(e => toast.showApiError(e, 'Failed to load institutions'));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await FeedbackService.inbox({
                status, type, institutionId, from: dayStartIso(from), to: dayStartIso(to, 1), page, size,
            });
            setRows(res.content ?? []);
            setTotal(res.totalElements ?? 0);
        } catch (error) {
            toast.showApiError(error, 'Failed to load feedback');
        } finally {
            setLoading(false);
        }
    }, [status, type, institutionId, from, to, page, size]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    const filterCell = { flex: '1 1 180px', maxWidth: 260 };

    return (
        <Box sx={{ p: 3 }} data-help-page="platform.feedback.page">
            <Typography variant="h5" sx={{ mb: 2 }}>User feedback</Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }} data-help-section="platform.feedback.filters">
                <TextField select size="small" label="Status" value={status} data-help="platform.feedback.filter.status"
                    onChange={e => { setStatus(e.target.value as FeedbackStatus | ''); setPage(0); }} sx={filterCell}>
                    <MenuItem value="">All</MenuItem>
                    {FEEDBACK_STATUSES.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Type" value={type} data-help="platform.feedback.filter.type"
                    onChange={e => { setType(e.target.value as FeedbackType | ''); setPage(0); }} sx={filterCell}>
                    <MenuItem value="">All</MenuItem>
                    {FEEDBACK_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                </TextField>
                <SearchableSelect data-help="platform.feedback.filter.institution" options={institutions} value={institutionId}
                    onChange={id => { setInstitutionId(id); setPage(0); }} label="Institution" size="small"
                    getOptionLabel={i => `${i.name} (${i.code})`} sx={{ flex: '1 1 240px', maxWidth: 360 }} />
                <TextField type="date" size="small" label="From" value={from} data-help="platform.feedback.filter.from"
                    onChange={e => { setFrom(e.target.value); setPage(0); }} sx={filterCell} slotProps={{ inputLabel: { shrink: true } }} />
                <TextField type="date" size="small" label="To" value={to} data-help="platform.feedback.filter.to"
                    onChange={e => { setTo(e.target.value); setPage(0); }} sx={filterCell} slotProps={{ inputLabel: { shrink: true } }} />
            </Box>

            {loading && <LinearProgress aria-label="Loading" sx={{ mb: 1 }} />}
            <TableContainer component={Paper} data-help-section="platform.feedback.table">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Sent</TableCell>
                            <TableCell>From</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Message</TableCell>
                            <TableCell data-help="platform.feedback.col.context">Page / build</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!loading && rows.length === 0 && (
                            <TableRow><TableCell colSpan={7} align="center">No feedback matches these filters.</TableCell></TableRow>
                        )}
                        {rows.map(r => (
                            <TableRow key={r.id} hover>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(r.createdAt)}</TableCell>
                                <TableCell>
                                    {r.username ?? '—'}
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                        {r.institutionName ?? 'No institution'}{r.roleNames ? ` · ${r.roleNames}` : ''}
                                    </Typography>
                                </TableCell>
                                <TableCell><Chip size="small" variant="outlined" label={typeLabel(r.type)} /></TableCell>
                                <TableCell sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', minWidth: 220 }}>
                                    {r.message}
                                    <FeedbackAttachmentChips feedbackId={r.id} attachments={r.attachments} platform />
                                    {r.reply && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                            Reply{r.repliedByUsername ? ` by ${r.repliedByUsername}` : ''}: {r.reply}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell sx={{ wordBreak: 'break-all', minWidth: 140 }}>
                                    {r.pagePath ?? '—'}
                                    {r.appVersion && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{r.appVersion}</Typography>
                                    )}
                                </TableCell>
                                <TableCell><FeedbackStatusChip status={r.status} /></TableCell>
                                <TableCell align="right">
                                    <Button size="small" onClick={() => setEditing(r)} data-help="platform.feedback.reply">Reply</Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={total}
                page={page}
                rowsPerPage={size}
                rowsPerPageOptions={[10, 20, 50, 100]}
                onPageChange={(_, p) => setPage(p)}
                onRowsPerPageChange={e => { setSize(parseInt(e.target.value, 10)); setPage(0); }}
            />

            {editing && (
                <ReplyDialog
                    row={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); }}
                />
            )}
        </Box>
    );
};

const ReplyDialog: React.FC<{ row: PlatformFeedback; onClose: () => void; onSaved: () => void }> = ({ row, onClose, onSaved }) => {
    const toast = useToast();
    const [reply, setReply] = useState('');
    const [status, setStatus] = useState<FeedbackStatus>(row.status === 'NEW' ? 'SEEN' : row.status);
    const [saving, setSaving] = useState(false);
    const tooLong = reply.length > FEEDBACK_MAX;

    const save = async () => {
        setSaving(true);
        try {
            const text = reply.trim();
            await FeedbackService.update(row.id, text ? { status, reply: text } : { status });
            toast.showSuccess(text ? 'Reply sent' : 'Status saved');
            onSaved();
        } catch (error) {
            toast.showApiError(error, 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open onClose={() => !saving && onClose()} fullWidth maxWidth="sm" data-help-page="platform.feedback.replyDialog">
            <DialogTitle>Reply to {row.username ?? 'user'}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{row.message}</Typography>
                <Box sx={{ mb: 2 }}><FeedbackAttachmentChips feedbackId={row.id} attachments={row.attachments} platform /></Box>
                {row.userAgent && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, wordBreak: 'break-all' }}>
                        {row.userAgent}
                    </Typography>
                )}
                {row.reply && (
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
                        Earlier reply: {row.reply}
                    </Typography>
                )}
                <TextField
                    label="Reply (the user gets it as a notification)"
                    data-help="platform.feedback.replyDialog.reply"
                    multiline minRows={3} fullWidth
                    value={reply}
                    onChange={e => setReply(e.target.value)}
                    error={tooLong}
                    helperText={`${reply.length} / ${FEEDBACK_MAX}`}
                    sx={{ mb: 2 }}
                />
                <TextField select size="small" label="Status" value={status} data-help="platform.feedback.replyDialog.status"
                    onChange={e => setStatus(e.target.value as FeedbackStatus)} sx={{ minWidth: 180 }}>
                    {FEEDBACK_STATUSES.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                </TextField>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button variant="contained" onClick={save} disabled={saving || tooLong}>
                    {saving ? 'Saving…' : reply.trim() ? 'Send reply' : 'Save status'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PlatformFeedbackPage;
